/**
 * vm-addon.js: power add-on in the PDP buy box (owner: PDP package).
 * Markup: blocks/vm-addon-cell.liquid. Spec: DESIGN-SPEC §4.h, CONVENTIONS §12.
 *
 * v5.3 (L4): the row no longer expands; no aria-expanded on the checkbox.
 * 1. Ticking the card updates Horizon's Add to cart label (main button and the
 *    sticky bar) to the running total and announces it (aria-live).
 * 2. When Horizon's product form in the same section dispatches its
 *    CartLinesUpdateEvent (action "add") and that add SUCCEEDS, this posts ONE
 *    /cart/add.js with items[] for the add-on variant, then dispatches its own
 *    CartLinesUpdateEvent carrying the cart and the rendered cart sections, so
 *    Horizon's drawer, cart items and cart bubble refresh with no extra fetch.
 * 3. A failed add leaves the jacket in the cart, shows an error with a retry.
 * Nothing here pre-ticks the box. Event names come from Horizon's own imports
 * (@shopify/events via the theme import map), never guessed strings.
 */
/*
 * v6.9 (2 Nov 2026): two-piece sets. When the block prints data-two-variants (",id,id,"), a
 * selected variant in that list adds TWO add-ons (one per piece): the row reads data-label-two,
 * the price and the running total double, and /cart/add.js gets quantity 2. The selected variant
 * is followed live (Horizon's productSelect, then the form's variant id). Without
 * data-two-variants nothing below changes: quantity 1, the row's text untouched.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const fill = (tpl, map) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));

class VmAddon extends HTMLElement {
  #ac = null;
  #mo = null;
  #busy = false;
  #queued = false;
  #picked = null;

  connectedCallback() {
    this.input = this.querySelector('.vm-addon__input');
    if (!this.input) return;
    this.section = this.closest('.shopify-section') || document.body;
    this.base = Number(this.dataset.basePrice) || 0;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.input.addEventListener('change', () => this.#toggled(), opts);
    this.querySelector('.vm-addon__retry')?.addEventListener('click', () => this.#add(), opts);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCart, opts);
    this.section.addEventListener(StandardEvents.productSelect, this.#onSelect, opts);
    // Quantity steppers and inputs: recompute the total once the event settles.
    for (const type of ['change', 'input', 'click']) this.section.addEventListener(type, this.#soon, opts);

    // Horizon re-renders the button text on variant change: re-apply the total.
    this.#mo = new MutationObserver(this.#soon);
    for (const el of this.section.querySelectorAll('product-form-component, sticky-add-to-cart')) {
      this.#mo.observe(el, { childList: true, subtree: true, characterData: true });
    }
    if (this.input.checked) this.#toggled(true);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#mo?.disconnect();
    if (this.input?.checked) {
      this.input.checked = false;
      this.#label();
    }
  }

  get #on() {
    return this.input.checked;
  }

  /** The variant the shopper has selected now. */
  #variantId() {
    if (this.#picked) return this.#picked;
    const input = this.section.querySelector('product-form-component input[name="id"]');
    return String(input?.value || this.dataset.baseVariant || '');
  }

  /** v6.9: 2 for a two-piece set variant (data-two-variants), else 1. */
  get #qty() {
    const two = this.dataset.twoVariants;
    if (!two) return 1;
    const id = this.#variantId();
    return id && two.includes(`,${id},`) ? 2 : 1;
  }

  /** Announcement text, the "two" wording when two banks are offered. */
  #text(key) {
    return (this.#qty === 2 && this.dataset[`${key}Two`]) || this.dataset[key];
  }

  /** v6.9: the row's name and price follow the selected variant (two-bank products only). */
  #sync() {
    if (!this.dataset.twoVariants) return;
    const two = this.#qty === 2;
    const name = this.querySelector('.vm-addon__name');
    const want = two ? this.dataset.labelTwo : this.dataset.labelOne;
    if (name && want && name.textContent !== want) name.textContent = want;
    const price = this.querySelector('.vm-addon__price');
    const cost = this.#fmt((Number(this.dataset.price) || 0) * (two ? 2 : 1));
    if (price && price.textContent !== cost) price.textContent = cost;
  }

  /** Batch label updates into one microtask (runs before paint, and in background tabs). */
  #soon = () => {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      this.#label();
    });
  };

  #fmt(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const text = formatMoney(Math.round(cents), format, this.dataset.currency || 'USD');
    return text.replace(/([.,])00(?!\d)/, '');
  }

  #total() {
    const qty = Number(this.section.querySelector('product-form-component input[name="quantity"]')?.value) || 1;
    return this.base * qty + (this.#on ? (Number(this.dataset.price) || 0) * this.#qty : 0);
  }

  /** Text nodes that carry the Add to cart wording (main button, sticky bar). */
  #nodes() {
    const out = [];
    const main = this.section.querySelector('product-form-component [ref="addToCartButton"]');
    const sticky = this.section.querySelector('sticky-add-to-cart .add-to-cart-text__content > span:first-child');
    for (const el of [main, sticky]) {
      if (!el || el.closest('button')?.disabled) continue;
      const node = [...el.childNodes].find((n) => n.nodeType === 3 && n.nodeValue.trim());
      if (node) out.push(node);
    }
    return out;
  }

  #label() {
    this.#sync();
    const total = this.#fmt(this.#total());
    for (const node of this.#nodes()) {
      // A value we didn't write means Horizon rendered fresh text: that is the new base.
      if (node._vmBase == null || node.nodeValue !== node._vmSet) node._vmBase = node.nodeValue;
      const want = this.#on ? fill(this.dataset.labelOn, { label: node._vmBase.trim(), total }) : node._vmBase;
      node._vmSet = want;
      if (node.nodeValue !== want) node.nodeValue = want;
    }
  }

  #say(text) {
    const live = this.querySelector('.vm-addon__status');
    if (!live || !text) return;
    live.textContent = '';
    setTimeout(() => (live.textContent = text), 60);
  }

  #error(show) {
    const box = this.querySelector('.vm-addon__error');
    if (box) box.hidden = !show;
  }

  #toggled(silent = false) {
    this.#error(false);
    this.#label();
    if (!silent) this.#say(fill(this.#text(this.#on ? 'sayOn' : 'sayOff'), { total: this.#fmt(this.#total()) }));
  }

  #onSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const price = detail?.resource?.price;
        if (price != null) this.base = Number(price) || this.base;
        const id = detail?.resource?.id;
        if (id != null) this.#picked = String(id);
        this.#soon();
      })
      .catch(() => {});
  };

  /** Horizon's own add from this section's product form: follow it with the add-on. */
  #onCart = (event) => {
    if (!this.#on || this.#busy || event.target === this) return;
    const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (!form || !this.section.contains(form)) return;
    if (event.action && event.action !== 'add') return;
    const lines = Array.isArray(event.lines) ? event.lines : [];
    if (lines.some((line) => String(line.merchandiseId) === this.dataset.variantId)) return;
    this.#busy = true;
    Promise.resolve(event.promise)
      .then((result) => (result?.detail?.didError ? (this.#busy = false) : this.#add()))
      .catch(() => (this.#busy = false));
  };

  async #add() {
    this.#busy = true;
    this.#error(false);
    const id = Number(this.dataset.variantId);
    const quantity = this.#qty;
    const deferred = CartLinesUpdateEvent.createPromise();
    let settled = false;
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity }],
        promise: deferred.promise,
      })
    );
    const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);
    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }], sections: sections.join(','), sections_url: location.pathname }),
      });
      const data = await response.json().catch(() => ({}));
      const failed = !response.ok || Boolean(data.status);
      const cart = await fetch(this.dataset.cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: failed ? 0 : quantity,
          sections: failed ? undefined : data.sections,
          source: 'vm-addon',
          sourceId: this.id,
          didError: failed,
        },
      });
      settled = true;
      if (failed) throw new Error(data.description || data.message || 'Add-on not added');
      this.input.checked = false;
      this.#label();
      this.#say(quantity === 2 ? this.dataset.sayAddedTwo || this.dataset.sayAdded : this.dataset.sayAdded);
    } catch (error) {
      if (!settled) deferred.reject(error);
      console.warn('[vm-addon]', error);
      this.#error(true);
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('vm-addon')) customElements.define('vm-addon', VmAddon);
